import { useId } from 'react';
import { LoaderCircle } from 'lucide-react';

import { isBlocked, isOnShift, type RiderProfile } from '@/api/types';
import { useRunSheet } from '@/hooks/use-deliveries';
import { primeLocationPermission } from '@/hooks/use-location-ping';
import { useSetOnShift } from '@/hooks/use-rider';
import { errorMessage } from '@/lib/errors';
import { Switch } from './ui/switch';
import { toast } from '@/lib/toast';

/**
 * The on/off-shift control, shared by the run sheet's bar and the account
 * screen. One widget for both, because two switches for one state is how they
 * end up disagreeing.
 *
 * Disabled outright while the rider is suspended or deactivated: neither is
 * theirs to lift, the backend refuses, and offering a switch that answers 403
 * is worse than not offering one.
 */
export function ShiftSwitch({ rider }: { rider: RiderProfile }) {
  const labelId = useId();
  const { isCarrying } = useRunSheet();
  const setOnShift = useSetOnShift();

  const on = isOnShift(rider);
  const blocked = isBlocked(rider);

  async function toggle(next: boolean) {
    /*
     * Going off shift with medicine still in the bag is refused HERE rather
     * than by the server, which has no opinion about it — a rider can be
     * OFFLINE and still be carrying. But the bag has to reach somebody: either
     * the customer, or the pharmacy via "Could not deliver". Simply going
     * offline leaves an order the board says is on its way and nobody is
     * carrying.
     */
    if (!next && isCarrying) {
      toast.error('Finish or hand back the order you are carrying first.');
      return;
    }

    try {
      await setOnShift.mutateAsync({ riderId: rider.id, onShift: next });

      // Going on shift is what starts the location stream, and the browser's
      // permission prompt belongs at that moment — while they are looking at
      // the phone deciding to work, not twenty seconds later on a bike.
      if (next) void primeLocationPermission();
    } catch (error) {
      toast.error(errorMessage(error));
    }
  }

  if (setOnShift.isPending) {
    return (
      <span className="grid size-control-md place-items-center" role="status">
        <LoaderCircle className="size-5 animate-spin text-ink-subtle motion-reduce:animate-none" aria-hidden="true" />
        <span className="sr-only-text">Changing your shift</span>
      </span>
    );
  }

  return (
    <span className="flex items-center gap-2">
      <span id={labelId} className="sr-only-text">
        {on ? 'On shift. Turn off to stop receiving jobs.' : 'Off shift. Turn on to receive jobs.'}
      </span>
      <Switch
        checked={on}
        disabled={blocked}
        aria-labelledby={labelId}
        onCheckedChange={(next) => void toggle(next)}
      />
    </span>
  );
}
